import React, { useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Box, Card, Title } from "@mantine/core";
import Collapsable from "common/admin/ui/Collapsable";
import { useGetSonometerQuery } from "../../api/sonometer.api";
import { SonometerAdminForm } from "../components/forms/SonometerAdminForm";
import {
  SonometerService,
  type ChampionshipSonometerRankingResponse,
} from "../../../services/sonometer.service";
import type { Sonometer } from "../../types/sonometer.model";

function SonometerAdminView({ isCreateMode }: { isCreateMode: boolean }) {
  const { sonometerId } = useParams<{ sonometerId: string }>();
  const location = useLocation();
  const isDuplicate = location.pathname.endsWith("/duplicate");

  // Ranking functionality state
  const [loading, setLoading] = useState(false);
  const [response, setResponse] =
    useState<ChampionshipSonometerRankingResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch sonometer data if not new
  const { data: sonometerData, isLoading } = useGetSonometerQuery(
    sonometerId!,
    {
      skip: isCreateMode || !sonometerId, // Fetch data if it's an existing sonometer
    }
  );
  void isLoading;

  // Ranking functionality handler
  const handleCalculateRanking = async () => {
    if (!sonometerData?.id || !sonometerData?.championshipId) {
      setError("Sonometer data is not available");
      return;
    }

    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      const sonometerService = new SonometerService();
      const result =
        await sonometerService.calculateChampionshipSonometerRanking({
          championshipId: sonometerData.championshipId,
          sonometerId: sonometerData.id,
        });
      setResponse(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Form Section - Always show form */}
      <SonometerAdminForm sonometer={sonometerData} editMode={!isCreateMode} />

      {/* Sonometers Championship Ranking Section */}
      {!isCreateMode && !isDuplicate && sonometerData && (
        <Collapsable title="Results">
          <Card shadow="sm" p="xl" mb="md">
            <Title order={4} mb="md">
              Sonometers Championship Ranking
            </Title>

            <div style={{ marginBottom: "20px" }}>
              <button
                onClick={handleCalculateRanking}
                disabled={loading}
                style={{
                  padding: "8px 16px",
                  backgroundColor: loading ? "#ccc" : "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: loading ? "not-allowed" : "pointer",
                }}
              >
                {loading ? "Calculating..." : "Get Top 3 Sonometers"}
              </button>
            </div>

            {error && (
              <div style={{ color: "red", marginBottom: "20px" }}>
                Error: {error}
              </div>
            )}

            {response && (
              <div>
                <h2>Response:</h2>
                <div
                  style={{
                    backgroundColor: "#f5f5f5",
                    padding: "15px",
                    borderRadius: "4px",
                    fontFamily: "monospace",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {JSON.stringify(response, null, 2)}
                </div>

                <div style={{ marginTop: "20px" }}>
                  <h3>Summary:</h3>
                  <p>
                    <strong>Championship ID:</strong> {response.championshipId}
                  </p>
                  <p>
                    <strong>Total Sonometers:</strong>{" "}
                    {response.totalSonometers}
                  </p>

                  {response.rank1Sonometers.length > 0 && (
                    <div style={{ marginTop: "15px" }}>
                      <h4>Top Sonometers:</h4>
                      <ul>
                        {[
                          ...response.rank1Sonometers,
                          ...response.rank2Sonometers,
                          ...response.rank3Sonometers,
                        ].map(
                          (
                            sonometer: Sonometer & {
                              rank: number;
                            }
                          ) => (
                            <li
                              key={sonometer.id}
                              style={{
                                marginBottom: "10px",
                              }}
                            >
                              <strong>Rank {sonometer.rank}:</strong>{" "}
                              {sonometer.opponentName}
                              (Score: {sonometer.score}, ID: {sonometer.id})
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Card>
        </Collapsable>
      )}

      {/* Data Dump Section
      {!isNew && !isDuplicate && sonometerId && (
        <Card shadow="sm" p="xl" mb="md">
          <Title order={4} mb="md">
            Sonometer Data Dump
          </Title>

          {isLoading && <div>Loading sonometer data...</div>}
          {error && (
            <Alert color="red" title="Error loading sonometer data">
              {JSON.stringify(error, null, 2)}
            </Alert>
          )}
          {sonometerData ? (
            <div>
              <Code
                block
                style={{
                  whiteSpace: "pre-wrap",
                  fontSize: "12px",
                  maxHeight: "400px",
                  overflow: "auto",
                }}
              >
                {JSON.stringify(sonometerData, null, 2)}
              </Code>

              <div style={{ marginTop: "16px" }}>
                <Title order={5} mb="sm">
                  Summary:
                </Title>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "8px",
                  }}
                >
                  <div>
                    <strong>ID:</strong> {sonometerData.id}
                  </div>
                  <div>
                    <strong>Opponent Name:</strong> {sonometerData.opponentName}
                  </div>
                  <div>
                    <strong>Championship ID:</strong>{" "}
                    {sonometerData.championshipId}
                  </div>
                  <div>
                    <strong>Score:</strong> {sonometerData.score}
                  </div>
                  <div>
                    <strong>Created At:</strong>{" "}
                    {new Date(sonometerData.createdAt).toLocaleString()}
                  </div>
                  <div>
                    <strong>Partition ID:</strong> {sonometerData.partitionId}
                  </div>
                  <div>
                    <strong>Owner L1:</strong> {sonometerData.owner.L1}
                  </div>
                  <div>
                    <strong>Animations:</strong>{" "}
                    {sonometerData.animations?.length || 0}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            !isLoading && <div>No data available for this sonometer</div>
          )}
        </Card>
      )} */}

      {/* <div>{sonometerData?.score}</div> */}
    </Box>
  );
}

export default SonometerAdminView;
